import type { ReactElement } from 'react';
import { connect } from 'react-redux';

import { formatReadableDateShort, safeParseISO } from 'shared/util/datetime-fns';
import type { Filters, PaleState } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import { generateDateText } from '../filters/travelTime/TravelMonths';

export type HotelFilterPreviewProps = Pick<
    Filters,
    'origin' | 'departureDate' | 'returnDate' | 'travelMonths' | 'duration' | 'ignoredFilters'
>;

export const HotelFilterPreview = ({
    origin,
    departureDate,
    returnDate,
    travelMonths,
    duration,
    ignoredFilters,
}: HotelFilterPreviewProps) => {
    const originDisplay = displayValues.originAirports.find((displayOrigin) => displayOrigin.value === origin);
    const parsedDepartureDate = safeParseISO(departureDate?.date);
    const parsedReturnDate = safeParseISO(returnDate?.date);

    const dateInfo = (): ReactElement => {
        if (parsedDepartureDate && parsedReturnDate && !ignoredFilters.includes('departureDate')) {
            return (
                <div data-testid="hotelFilterPreview-dateInfo-fixed">
                    <span className="u-no-break">{formatReadableDateShort(parsedDepartureDate)}</span>
                    <span className="icon-pre icon--one-way-arrow u-mr8 u-ml8" />
                    <span className="u-no-break">{formatReadableDateShort(parsedReturnDate)}</span>
                </div>
            );
        }
        if (travelMonths.length !== 0 || (duration && !ignoredFilters.includes('travelMonths'))) {
            return <div data-testid="hotelFilterPreview-dateInfo-flex">{generateDateText(travelMonths, duration)}</div>;
        }
        return <div data-testid="hotelFilterPreview-dateInfo-default">Når som helst</div>;
    };

    return (
        <div data-testid="hotelFilterPreview">
            <div className="u-caption" data-testid="hotelFilterPreview-origin">
                {originDisplay?.text}
            </div>
            {dateInfo()}
        </div>
    );
};

const mapStateToProps = (state: PaleState): HotelFilterPreviewProps => ({
    origin: state.filters.origin,
    departureDate: state.filters.departureDate,
    returnDate: state.filters.returnDate,
    ignoredFilters: state.filters.ignoredFilters,
    travelMonths: state.filters.travelMonths,
    duration: state.filters.duration,
});

export default connect(mapStateToProps)(HotelFilterPreview);
